import { classifyCheckin } from '@peekapak/types/EmotionsClassification';
import type { Mood, SecondaryMood } from '@peekapak/types/Mood';
import { resolveSixToTwelveCategory } from '../ConcerningEmotionsReportUtilities';
import type { SnapshotCheckIn } from './moodboardSnapshot';
import type { GradeBand } from './WeeklyClassSnapshot/WeeklyClassSnapshot';

type KeysOf = (checkIn: SnapshotCheckIn) => string[];

export const byMood: KeysOf = (checkIn) => [checkIn.mood];
export const byFamily: KeysOf = (checkIn) => {
  const family = resolveSixToTwelveCategory(checkIn.mood as SecondaryMood);
  return family ? [family] : [];
};
export const bySource: KeysOf = (checkIn) => checkIn.sources;

const MONTHS = ['Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];

export function getSchoolYearStart(now: number): number {
  const date = new Date(now);
  return new Date(date.getMonth() >= 7 ? date.getFullYear() : date.getFullYear() - 1, 7, 1).getTime();
}

// August to June, the months the chart shows
export function inSchoolYear(checkIns: SnapshotCheckIn[], now: number): SnapshotCheckIn[] {
  const start = getSchoolYearStart(now);
  const end = new Date(new Date(start).getFullYear() + 1, 6, 1).getTime();
  return checkIns.filter(
    (checkIn) => checkIn.createdAt >= start && checkIn.createdAt < end && checkIn.createdAt <= now,
  );
}

export interface MonthTotals {
  label: string;
  status: 'past' | 'current' | 'upcoming';
  counts: Record<string, number>;
  checkInCount: number;
}

export function getMonthlyTotals(checkIns: SnapshotCheckIn[], keysOf: KeysOf, now: number): MonthTotals[] {
  const startYear = new Date(getSchoolYearStart(now)).getFullYear();
  return MONTHS.map((label, i) => {
    const start = new Date(startYear, 7 + i, 1).getTime();
    const end = new Date(startYear, 8 + i, 1).getTime();
    const inMonth = checkIns.filter((checkIn) => checkIn.createdAt >= start && checkIn.createdAt < end);
    const counts: Record<string, number> = {};
    inMonth.flatMap(keysOf).forEach((key) => (counts[key] = (counts[key] ?? 0) + 1));
    const status = start > now ? 'upcoming' : end <= now ? 'past' : 'current';
    return { label, status, counts, checkInCount: inMonth.length };
  });
}

export interface CountRow {
  key: string;
  count: number;
  percent: number;
}

// Most first, ties alphabetical; allKeys adds keys with no check-ins at 0%
export function countBy(
  checkIns: SnapshotCheckIn[],
  keysOf: KeysOf,
  allKeys: readonly string[] = [],
): { total: number; rows: CountRow[] } {
  const counts = new Map<string, number>(allKeys.map((key) => [key, 0]));
  checkIns.flatMap(keysOf).forEach((key) => counts.set(key, (counts.get(key) ?? 0) + 1));
  const total = [...counts.values()].reduce((sum, count) => sum + count, 0);
  const rows = [...counts.entries()]
    .sort(([a, countA], [b, countB]) => countB - countA || a.localeCompare(b))
    .map(([key, count]) => ({ key, count, percent: total ? Math.round((count * 100) / total) : 0 }));
  return { total, rows };
}

const TYPICAL_UP_PERCENT: Record<GradeBand, [number, number]> = { elementary: [75, 85], secondary: [50, 60] };

export function getUpDown(checkIns: SnapshotCheckIn[], gradeBand: GradeBand) {
  if (checkIns.length === 0) return undefined;
  const band = gradeBand === 'elementary' ? 'PK-5' : '6-12';
  const up = checkIns.filter((checkIn) => classifyCheckin(checkIn.mood as Mood, band) === 'not-concerning').length;
  const upPercent = Math.round((up * 100) / checkIns.length);
  const [low, high] = TYPICAL_UP_PERCENT[gradeBand];
  const comparedToTypical = upPercent > high ? 'above' : upPercent < low ? 'below' : 'within';
  return { upPercent, comparedToTypical } as const;
}

export function getTopPairs(checkIns: SnapshotCheckIn[]) {
  const pairs = new Map<string, { feeling: string; source: string; count: number }>();
  checkIns.forEach((checkIn) =>
    checkIn.sources.forEach((source) => {
      const key = `${checkIn.mood}|${source}`;
      const pair = pairs.get(key) ?? { feeling: checkIn.mood, source, count: 0 };
      pairs.set(key, { ...pair, count: pair.count + 1 });
    }),
  );
  return [...pairs.entries()]
    .sort(([a, pairA], [b, pairB]) => pairB.count - pairA.count || a.localeCompare(b))
    .slice(0, 5)
    .map(([, pair]) => pair);
}
